import { useCallback, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BadgeCheck, Plus, ShieldMinus } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { DangerDialog } from '@/components/DangerDialog'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Tag } from '@/components/StatusBadge'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import {
  fetchVerifiedRegistry,
  grantVerification,
  revokeVerified,
  VERIF_STR as S,
  type VerifiedRow,
} from './verifApi'

/**
 * A9.07 + A9.08 — Verifikatsiya berish va olib tashlash. Tasdiqlanganlar
 * reyestri (live GET /admin/verified) ustidan ikki amal: qatordan «Olib
 * tashlash» (A9.08, live POST /admin/verified/:id/revoke — DangerDialog L2) va
 * sarlavhadan «Berish» (A9.07, POST /admin/verified/grant — greenfield fail-soft).
 */
export function VerificationManage() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [target, setTarget] = useState<VerifiedRow | null>(null)
  const [grantOpen, setGrantOpen] = useState(false)
  const mayManage = can(PERMISSIONS.USERS_BAN)

  const columns = useMemo<AdminColumn<VerifiedRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id', width: 80 },
      {
        key: 'username',
        header: S.user,
        type: 'text',
        minWidth: 200,
        sortable: false,
        render: (r) => (
          <span className="flex items-center gap-1.5">
            <Link to={`/users/${r.id}`} className="text-accent hover:underline">
              {r.first_name || (r.username ? `@${r.username}` : `#${r.id}`)}
            </Link>
            {r.is_bot && <Tag>bot</Tag>}
          </span>
        ),
      },
      {
        key: 'username',
        header: S.username,
        type: 'text',
        width: 160,
        render: (r) => (r.username ? `@${r.username}` : '—'),
      },
      { key: 'created_at', header: S.registered, type: 'date', width: 160 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<VerifiedRow>[]>(
    () => [
      {
        id: 'revoke',
        label: S.revoke,
        icon: <ShieldMinus size={14} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: (r) => setTarget(r),
      },
    ],
    [],
  )

  const fetcher = useCallback(fetchVerifiedRegistry, [])

  return (
    <>
      <PageHeader
        title={S.manage}
        screenId="A9.08"
        description={S.manageNote}
        actions={
          <div className="flex items-center gap-1.5">
            {mayManage && (
              <button
                onClick={() => setGrantOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
              >
                <Plus size={13} />
                {S.grant}
              </button>
            )}
            <Link
              to="/verification/registry"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <BadgeCheck size={13} />
              {S.registered}
            </Link>
          </div>
        }
      />
      <DataTable<VerifiedRow>
        screenId="A9.08"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <BadgeCheck size={28} strokeWidth={1.5} /> }}
      />

      {target && (
        <DangerDialog
          open
          onClose={() => setTarget(null)}
          screenId="A9.08"
          level={2}
          title={S.revokeTitle(target.first_name || (target.username ? `@${target.username}` : `#${target.id}`))}
          description={S.revokeDesc}
          confirmLabel={S.revoke}
          onConfirm={async () => {
            try {
              await revokeVerified(target.id)
              toast(S.revoked, { tone: 'success' })
            } catch (e) {
              toast(S.failed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}

      {grantOpen && <GrantDialog onClose={() => setGrantOpen(false)} toast={toast} />}
    </>
  )
}

function GrantDialog({
  onClose,
  toast,
}: {
  onClose: () => void
  toast: ReturnType<typeof useToast>['toast']
}) {
  const [userId, setUserId] = useState('')
  const [kind, setKind] = useState('identity')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const submit = async () => {
    const id = Number(userId)
    if (!Number.isInteger(id) || id <= 0) return
    setBusy(true)
    const r = await grantVerification(id, kind, note)
    setBusy(false)
    if (r.ok) {
      toast(S.granted, { tone: 'success' })
      onClose()
    } else {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={S.grantTitle}
      icon={<BadgeCheck size={18} className="text-accent" />}
      initialFocusRef={inputRef}
      footer={
        <>
          <Button onClick={onClose}>{S.back}</Button>
          <Button variant="primary" disabled={busy || !userId.trim()} onClick={submit}>
            {S.grantSubmit}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs text-tertiary">{S.grantUserId}</span>
          <input
            ref={inputRef}
            value={userId}
            onChange={(e) => setUserId(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
            placeholder="123456"
            className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-tertiary">{S.kind}</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          >
            <option value="identity">{S.kindIdentity}</option>
            <option value="org">{S.kindOrg}</option>
            <option value="notable">{S.kindNotable}</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-tertiary">{S.note}</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
        </label>
      </div>
    </Dialog>
  )
}
